import {Dispatch, useCallback, useEffect, useReducer} from 'react';
import {Dialog, Heading, IconButton, Pane, RefreshIcon} from "evergreen-ui";
import {ImportUserRequestListAction} from "./importUserRequestListReducer";
import {CloseButton, LoadingSpinner, Paths, RelayConnection, ErrorDisplay} from "common";
import {get} from "lodash";
import {ImportStatusNumToString, ImportUserRequestInfo, ProviderUserImportInfo} from "../domain/types";
import {gql, useQuery} from "@apollo/client";
import {RelayPagination, useRelayPagination} from "providerapp/pagination";
import {importUserRequestDetailReducer, initState} from "./importUserRequestDetailReducer";
import {USER_POPUP_HEIGHT} from "../component/const";
import {FeatureFlaggable, ProviderPortalFeatureFlag, useFeatureFlagFilter} from "featureflag";

type ProviderUserImportInfoColumn = FeatureFlaggable<{
    name: string;
    type: "text" | "boolean"
    attr: Paths<ProviderUserImportInfo>;
}>;

export const ProviderUserImportInfoListColumn: Readonly<ProviderUserImportInfoColumn[]> = [
    {name: 'Email', attr: 'email', type: "text"},
    {name: 'First Name', attr: 'firstName', type: "text"},
    {name: 'Last Name', attr: 'lastName', type: "text"},
    {name: 'Client Placement', attr: 'referralManagementAccess', type: "boolean"},
    {name: 'Client Care', attr: 'clientCareAccess', type: "boolean"},
    {name: 'Provider Metrics', attr: 'providerMetricsAccess', type: "boolean"},
    {name: 'Roster Management', attr: 'rosterAdministrationAccess', type: "boolean"},
    {name: 'User Administration', attr: 'userManagementAccess', type: 'boolean'},
    // Before Status, so the two outcome columns stay last. Both flag names read backwards, hence negate.
    {
        name: 'Audit Log', attr: 'auditLogAccess', type: 'boolean',
        featureFlag: ProviderPortalFeatureFlag.TDTL_9900_IS_EXPORT_DISABLED,
        featureFlagNegate: true,
    },
    {
        name: 'Contact Management Edit', attr: 'contactEditAccess', type: 'boolean',
        featureFlag: ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED,
        featureFlagNegate: true,
    },
    {
        name: 'Contact Management', attr: 'contactReadAccess', type: 'boolean',
        featureFlag: ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED,
        featureFlagNegate: true,
    },
    {name: 'Status', attr: 'importStatus', type: 'text'},
    {name: 'Failed Reason', attr: 'oktaApiError', type: 'text'},
]


export const USER_IMPORT_REQUEST_DETAIL = gql`
query userImports($importRequestGuid: UUID, $first: Int = 20, $after: String) {
    userImports(
            importRequestGuid: $importRequestGuid
            first: $first
            after: $after
    ) {
        edges {
            cursor
            node {
                firstName
                lastName
                email

                referralManagementAccess
                clientCareAccess
                providerMetricsAccess
                rosterAdministrationAccess
                userManagementAccess
                auditLogAccess
                contactEditAccess
                contactReadAccess
                
                oktaApiError
                importStatus
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
}
`

type ImportUserRequestDetailResponse = RelayConnection<ProviderUserImportInfo> & { totalElements: number }

type UserImportHistoryRecordDetailProps = {
    show: boolean;
    selectedRequest: ImportUserRequestInfo | null;
    importListDispatch: Dispatch<ImportUserRequestListAction>;
}

export const UserImportRequestDetail = ({
                                            show,
                                            importListDispatch,
                                            selectedRequest
                                        }: UserImportHistoryRecordDetailProps) => {
    const closePopup = useCallback(() => {
        importListDispatch({type: "SET_HIDE_DETAIL"});
    }, [importListDispatch]);

    const [{
        refetchFunc,
        pagination
    }, dispatch] = useReducer(importUserRequestDetailReducer, initState())

    const {
        loading,
        data,
        error,
        refetch
    } = useQuery<{ userImports: ImportUserRequestDetailResponse }, {}>(
        USER_IMPORT_REQUEST_DETAIL, {
            notifyOnNetworkStatusChange: true,
            skip: selectedRequest === null,
            variables: {
                importRequestGuid: selectedRequest?.guid,
                ...pagination.toGraphQlParams(),
            },
            onCompleted: () => {
                dispatch({type: "SET_REFRESH_FUNC", refetchFunc: refetch})
            }
        }
    );

    useEffect(() => {
        // auto refresh data on component mount
        if (show && refetchFunc) {
            refetchFunc();
        }
    }, [show, refetchFunc]);

    let totalRequests: number = 0;
    if (data?.userImports?.pageInfo?.endCursor !== null) {
        totalRequests = data?.userImports?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalRequests);
    const columns = useFeatureFlagFilter(ProviderUserImportInfoListColumn);
    if (selectedRequest === null) {
        return <div/>;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Error loading user import request detail" />;
    }


    return (
        <Dialog
            isShown={show}
            header={
                <Pane
                    className="w-full flex justify-between">
                    <Heading size={600} fontWeight="bolder">Import Detail ({selectedRequest?.total})</Heading>
                    <CloseButton className="mr-2" onClick={closePopup}/>
                </Pane>
            }
            hasHeader={true}
            hasFooter={false}
            width={'70%'}
            containerProps={{
                maxHeight: USER_POPUP_HEIGHT,
                minHeight: 600,
                marginY: `calc((100vh - ${USER_POPUP_HEIGHT}px) / 2)`,
                overflowY: 'auto'
            }}
            onCloseComplete={closePopup}
        >
            <div className="flex justify-end pb-4">
                <RelayPagination pageInfo={data?.userImports.pageInfo} count={totalRequests}
                                 onClick={onPaginationClick}/>
                <IconButton intent='success'
                            marginX={8}
                            icon={RefreshIcon}
                            iconSize={18}
                            disabled={loading}
                            onClick={() => refetchFunc && refetchFunc()}
                />
            </div>
            <div className="pb-4">
                <table className="table w-full">
                    <thead>
                    <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                        {
                            columns.map((column) => (
                                <th
                                    key={column.attr}
                                    className={'font-light' + (column.attr === 'email' ? ' pl-8' : '')}
                                >
                                    <div className='flex items-center'>
                                        <div>
                                            {column.name}
                                        </div>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        !loading && data!.userImports.edges
                            .map((edge) => edge.node)
                            .map((row) => (
                                <ImportUserDetailPreview key={row.email} record={row} columns={columns}/>
                            ))
                    }
                    </tbody>
                </table>
                {
                    loading &&
                    <div className="pt-8">
                        <div className="w-full h-full flex flex-grow items-center">
                            <LoadingSpinner spacing={20} height={"100%"}/>
                        </div>
                    </div>
                }
            </div>
        </Dialog>
    );
}
type ImportUserDetailPreviewProps = {
    record: ProviderUserImportInfo;
    columns: Readonly<ProviderUserImportInfoColumn[]>;
}
const ImportUserDetailPreview = ({record, columns}: ImportUserDetailPreviewProps) => {
    const displayColumn = (column: ProviderUserImportInfoColumn) => {
        if (column.type === "boolean") {
            return <>
                {get(record, column.attr) ? 'Yes' : 'No'}
            </>
        }
        if (column.attr === "importStatus") {
            return <>
                {ImportStatusNumToString.get(get(record, column.attr))}
            </>
        }

        return <>{get(record, column.attr)}</>
    }

    const hasFailed = record.oktaApiError !== null

    return (
        <tr className={`border-b border-gray-200 relative hover:${hasFailed ? "bg-red-300" : "bg-gray-200"} ${hasFailed ? "bg-red-200" : ""}`}>
            {
                columns.map((column) => (
                    <td
                        key={column.name}
                        className={'py-5 truncate' + (column.attr === 'email' ? ' pl-8' : '')}
                    >
                        {
                            displayColumn(column)
                        }
                    </td>
                ))
            }
        </tr>
    );
}
